import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportModal } from '../../../components/feature/post/ReportModal';
import EmbedPlayer, { detectEmbedType } from '../../artist/components/EmbedPlayer';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import { ResonateModal } from '../../artist/components/ResonateModal';
import { LevelUpOverlay } from '../../artist/components/LevelUpOverlay';
import { useResonanceLevelUp } from '../../../hooks/useResonanceLevelUp';

interface PostCardProps {
  id: string;
  artistId: string;
  artistName: string;
  artistAvatar: string;
  postDate: string;
  imageUrl: string;
  content: string;
  requiredFanLevel: number;
  supportTotal: number;
  isLocked: boolean;
  authorType?: 'maker' | 'node';
  is_hidden?: boolean;
  isUnlockedTab?: boolean;
  savedPostId?: string;
  onUnsave?: () => void;
}

// URLをリンク化する関数
function linkifyText(text: string): JSX.Element[] {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className="text-neon-green hover:underline transition-colors"
          style={{ color: '#00ff41' }}
        >
          {part}
        </a>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

export function PostCard({
  id,
  artistId,
  artistName,
  artistAvatar,
  postDate,
  imageUrl,
  content,
  requiredFanLevel,
  supportTotal,
  isLocked,
  authorType,
  is_hidden = false,
  isUnlockedTab = false,
  savedPostId,
  onUnsave,
}: PostCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [showHiddenContent, setShowHiddenContent] = useState(false);

  // アクション状態
  const [isEchoed, setIsEchoed] = useState(false);
  const [isSaved, setIsSaved] = useState(!!savedPostId);
  const [savedId, setSavedId] = useState<string | null>(savedPostId || null);
  const [echoAnim, setEchoAnim] = useState(false);
  const [saveAnim, setSaveAnim] = useState(false);
  const [savingInProgress, setSavingInProgress] = useState(false);
  const [echoInProgress, setEchoInProgress] = useState(false);

  // RESONATE モーダル
  const [isResonateModalOpen, setIsResonateModalOpen] = useState(false);
  const [localSupportTotal, setLocalSupportTotal] = useState(supportTotal);

  // レベルアップ演出
  const { levelUpInfo, checkLevelUp, clearLevelUp } = useResonanceLevelUp();
  // アーティストのアクセントカラーをSupabaseから取得（キャッシュ）
  const [artistColor, setArtistColor] = useState('#ff006e');

  const isNode = authorType === 'node';

  // メディアタイプを判定
  const mediaType = imageUrl ? detectEmbedType(imageUrl) : 'none';
  const isVideoEmbed = mediaType !== 'none';
  const isImageUrl = imageUrl && !isVideoEmbed;

  // マウント時にSAVE状態をDBから確認
  useEffect(() => {
    if (!user || savedPostId) return;
    const checkSaved = async () => {
      const { data } = await supabase
        .from('saved_posts')
        .select('id')
        .eq('user_id', user.id)
        .eq('post_id', id)
        .maybeSingle();
      if (data) {
        setIsSaved(true);
        setSavedId(data.id);
      }
    };
    checkSaved();
  }, [user, id, savedPostId]);

  // マウント時にECHO状態をDBから確認
  useEffect(() => {
    if (!user) return;
    const checkEchoed = async () => {
      const { data } = await supabase
        .from('post_echoes')
        .select('id')
        .eq('user_id', user.id)
        .eq('post_id', id)
        .maybeSingle();
      if (data) setIsEchoed(true);
    };
    checkEchoed();
  }, [user, id]);

  // アーティストカラー取得
  useEffect(() => {
    if (!artistId || isNode) return;
    supabase
      .from('artist_layers')
      .select('color')
      .eq('user_id', artistId)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.color) setArtistColor(data.color);
      });
  }, [artistId, isNode]);

  const handleResonateSuccess = async () => {
    setLocalSupportTotal((prev) => prev + 100);
    // レベルアップ検知
    if (user && artistId && !isNode) {
      await checkLevelUp(user.id, artistId, artistName, artistColor);
    }
  };

  const handleArtistClick = () => {
    if (isNode) {
      navigate(`/shop/${artistId}`);
    } else {
      navigate(`/artist/${artistId}`);
    }
  };

  const handleShare = async () => {
    const text = `"${artistName}" の限定コンテンツをDIGNIAでチェック！🔥 #DIGNIA`;
    const url = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'DIGNIA', text, url });
      } catch (err) {
        console.log('Share canceled or failed', err);
      }
    } else {
      window.open(
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
        '_blank'
      );
    }
  };

  const handleEcho = async () => {
    if (!user || echoInProgress) return;
    setEchoAnim(true);
    setTimeout(() => setEchoAnim(false), 600);
    setEchoInProgress(true);

    if (isEchoed) {
      // ECHO解除
      setIsEchoed(false);
      try {
        await supabase
          .from('post_echoes')
          .delete()
          .eq('user_id', user.id)
          .eq('post_id', id);
      } catch (err) {
        console.error('ECHO解除に失敗:', err);
        setIsEchoed(true); // ロールバック
      }
    } else {
      // ECHO追加
      setIsEchoed(true);
      try {
        await supabase
          .from('post_echoes')
          .insert({ user_id: user.id, post_id: id });
      } catch (err) {
        console.error('ECHOに失敗:', err);
        setIsEchoed(false); // ロールバック
      }
    }
    setEchoInProgress(false);
  };

  const handleSave = async () => {
    if (!user || savingInProgress) return;
    setSaveAnim(true);
    setTimeout(() => setSaveAnim(false), 500);

    if (isSaved) {
      if (onUnsave) {
        onUnsave();
        setIsSaved(false);
        setSavedId(null);
        return;
      }
      if (savedId) {
        setSavingInProgress(true);
        try {
          await supabase.from('saved_posts').delete().eq('id', savedId);
          setIsSaved(false);
          setSavedId(null);
        } catch (err) {
          console.error('保存解除に失敗:', err);
        } finally {
          setSavingInProgress(false);
        }
      }
    } else {
      setSavingInProgress(true);
      try {
        const { data, error } = await supabase
          .from('saved_posts')
          .insert({ user_id: user.id, post_id: id })
          .select('id')
          .maybeSingle();
        if (!error && data) {
          setIsSaved(true);
          setSavedId(data.id);
        }
      } catch (err) {
        console.error('保存に失敗:', err);
      } finally {
        setSavingInProgress(false);
      }
    }
  };

  // ── 非表示状態のUI ──────────────────────────────────────────
  if (is_hidden && !showHiddenContent) {
    return (
      <>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: 'rgba(10, 8, 8, 0.85)',
            border: '1px solid rgba(255, 60, 0, 0.35)',
            boxShadow: '0 0 30px rgba(255, 40, 0, 0.08), inset 0 0 60px rgba(0,0,0,0.6)',
            opacity: 0.75,
          }}
        >
          <div
            className="h-px w-full"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,60,0,0.8) 30%, rgba(255,160,0,0.9) 50%, rgba(255,60,0,0.8) 70%, transparent 100%)',
              boxShadow: '0 0 8px rgba(255,80,0,0.6)',
            }}
          />
          <div className="p-4 sm:p-6 flex items-center gap-3">
            <button
              onClick={handleArtistClick}
              className="w-12 h-12 rounded-full overflow-hidden border-2 flex-shrink-0 cursor-pointer"
              style={{ borderColor: 'rgba(255,60,0,0.2)', filter: 'grayscale(1) blur(2px)', opacity: 0.4 }}
            >
              <img src={artistAvatar} alt="hidden" className="w-full h-full object-cover" />
            </button>
            <div className="flex-1 min-w-0">
              <button
                onClick={handleArtistClick}
                className="text-sm font-bold tracking-widest cursor-pointer"
                style={{ color: 'rgba(255,100,50,0.4)', filter: 'blur(3px)', userSelect: 'none' }}
              >
                {artistName}
              </button>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.15)' }}>
                {postDate}
              </p>
            </div>
            <div
              className="flex items-center gap-1.5 px-3 py-1 rounded"
              style={{ background: 'rgba(255,40,0,0.12)', border: '1px solid rgba(255,60,0,0.5)' }}
            >
              <i className="ri-eye-off-line text-xs" style={{ color: '#ff4020' }} />
              <span className="text-[10px] font-black tracking-[0.2em] uppercase" style={{ color: '#ff4020' }}>
                CENSORED
              </span>
            </div>
          </div>
          <div className="px-4 sm:px-6 pb-2">
            <div
              className="relative rounded-xl p-6 flex flex-col items-center gap-4 text-center overflow-hidden"
              style={{ background: 'rgba(20, 5, 0, 0.7)', border: '1px solid rgba(255,60,0,0.25)' }}
            >
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden" aria-hidden="true">
                <span className="text-[80px] font-black tracking-widest opacity-[0.04]" style={{ color: '#ff3000', letterSpacing: '0.3em' }}>HIDDEN</span>
              </div>
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                className="w-14 h-14 flex items-center justify-center rounded-full flex-shrink-0"
                style={{ background: 'rgba(255,50,0,0.1)', border: '1px solid rgba(255,60,0,0.4)', boxShadow: '0 0 20px rgba(255,40,0,0.2)' }}
              >
                <i className="ri-alarm-warning-fill text-2xl" style={{ color: '#ff5020' }} />
              </motion.div>
              <div className="relative z-10 space-y-1.5">
                <p className="text-xs font-black tracking-[0.25em] uppercase" style={{ color: 'rgba(255,100,50,0.7)' }}>⚠ CONTENT WARNING</p>
                <p className="text-sm font-bold leading-relaxed" style={{ color: 'rgba(255,220,200,0.85)' }}>
                  多数のユーザーからの通報により、<br />このエコーは非表示にされました。
                </p>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>コミュニティガイドライン違反の可能性があります</p>
              </div>
              <div className="w-full h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,60,0,0.3), transparent)' }} />
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setShowHiddenContent(true)}
                className="flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap transition-all duration-200"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,60,0,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,60,0,0.4)'; e.currentTarget.style.color = 'rgba(255,160,100,0.9)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.color = 'rgba(255,255,255,0.45)'; }}
              >
                <i className="ri-eye-line text-sm" />
                内容を見る (Show Anyway)
              </motion.button>
            </div>
          </div>
          <div className="p-4 sm:p-6 pt-3 flex items-center justify-between">
            <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: 'rgba(255,60,0,0.3)' }}>AUTO-MODERATED · DIG-DIM SYSTEM</p>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: '#ff3000', boxShadow: '0 0 6px #ff3000' }} />
              <span className="text-[10px] tracking-widest" style={{ color: 'rgba(255,60,0,0.5)' }}>FLAGGED</span>
            </div>
          </div>
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,60,0,0.5) 50%, transparent 100%)' }} />
        </motion.div>
        <ReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} postId={id} />
      </>
    );
  }

  // ── ロック投稿のコンパクト・チラ見せモード ──────────────────────────────────────────
  if (isLocked && !isUnlockedTab) {
    const levelColors: Record<number, { primary: string; glow: string; bg: string; label: string; icon: string }> = {
      1: { primary: '#4ade80', glow: 'rgba(74,222,128,0.35)', bg: 'rgba(74,222,128,0.08)', label: 'RESONATOR', icon: 'ri-seedling-line' },
      2: { primary: '#fb923c', glow: 'rgba(251,146,60,0.35)', bg: 'rgba(251,146,60,0.08)', label: 'DEEP DIVER', icon: 'ri-fire-line' },
      3: { primary: '#facc15', glow: 'rgba(250,204,21,0.45)', bg: 'rgba(250,204,21,0.08)', label: 'CORE FAN', icon: 'ri-vip-crown-line' },
    };
    const lc = levelColors[requiredFanLevel] ?? levelColors[1];

    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative rounded-2xl overflow-hidden"
        style={{
          background: 'rgba(8, 6, 12, 0.92)',
          border: `1px solid ${lc.primary}30`,
          boxShadow: `0 4px 32px rgba(0,0,0,0.5), 0 0 0 1px ${lc.primary}10`,
        }}
      >
        <div className="h-0.5 w-full" style={{ background: `linear-gradient(90deg, transparent, ${lc.primary}88, ${lc.primary}, ${lc.primary}88, transparent)`, boxShadow: `0 0 12px ${lc.glow}` }} />
        <div className="p-4 sm:p-5 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full overflow-hidden border-2 flex-shrink-0" style={{ borderColor: `${lc.primary}30`, filter: 'blur(3px) grayscale(0.6)', opacity: 0.5 }}>
            <img src={artistAvatar} alt="" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold tracking-widest select-none" style={{ color: `${lc.primary}55`, filter: 'blur(4px)' }}>{artistName}</p>
            <p className="text-xs mt-0.5 text-white/20 tracking-wide">{postDate}</p>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full flex-shrink-0" style={{ background: lc.bg, border: `1px solid ${lc.primary}55` }}>
            <span className="w-4 h-4 flex items-center justify-center"><i className={`${lc.icon} text-xs`} style={{ color: lc.primary }} /></span>
            <span className="text-[10px] font-black tracking-widest" style={{ color: lc.primary }}>LV.{requiredFanLevel}+</span>
          </div>
        </div>
        {imageUrl && (
          <div className="relative w-full" style={{ aspectRatio: '16/9' }}>
            <div className="absolute inset-0" style={{ backgroundImage: isImageUrl ? `url(${imageUrl})` : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(18px) brightness(0.35) saturate(0.6)', transform: 'scale(1.05)' }} />
            <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at center, ${lc.bg} 0%, rgba(8,6,12,0.7) 70%)` }} />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6">
              <motion.div animate={{ scale: [1, 1.08, 1], boxShadow: [`0 0 20px ${lc.glow}`, `0 0 40px ${lc.glow}, 0 0 80px ${lc.glow}`, `0 0 20px ${lc.glow}`] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }} className="w-16 h-16 flex items-center justify-center rounded-full" style={{ background: lc.bg, border: `2px solid ${lc.primary}66`, boxShadow: `0 0 24px ${lc.glow}` }}>
                <i className="ri-lock-fill text-2xl" style={{ color: lc.primary }} />
              </motion.div>
              <div className="text-center space-y-1.5">
                <p className="text-xs font-black tracking-[0.3em] uppercase" style={{ color: `${lc.primary}cc` }}>LOCKED CONTENT</p>
                <p className="text-white/70 text-sm font-bold leading-snug"><span style={{ color: lc.primary }}>ファンレベル {requiredFanLevel}</span>{' '}以上で解禁</p>
                <p className="text-xs tracking-wider font-bold uppercase" style={{ color: `${lc.primary}99` }}>{lc.label}</p>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${lc.primary}33` }}>
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line text-xs" style={{ color: `${lc.primary}99` }} /></span>
                <span className="text-[11px] tracking-wider" style={{ color: 'rgba(255,255,255,0.45)' }}>このアーティストをサポートしてレベルアップ</span>
              </div>
            </div>
            <div className="absolute top-3 left-3 w-5 h-5 border-t-2 border-l-2 rounded-tl" style={{ borderColor: `${lc.primary}44` }} />
            <div className="absolute top-3 right-3 w-5 h-5 border-t-2 border-r-2 rounded-tr" style={{ borderColor: `${lc.primary}44` }} />
            <div className="absolute bottom-3 left-3 w-5 h-5 border-b-2 border-l-2 rounded-bl" style={{ borderColor: `${lc.primary}44` }} />
            <div className="absolute bottom-3 right-3 w-5 h-5 border-b-2 border-r-2 rounded-br" style={{ borderColor: `${lc.primary}44` }} />
          </div>
        )}
        <div className="px-5 py-4 relative overflow-hidden">
          <p className="text-sm leading-relaxed select-none" style={{ color: 'rgba(255,255,255,0.5)', filter: 'blur(6px)', maskImage: 'linear-gradient(to bottom, white 30%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, white 30%, transparent 100%)', userSelect: 'none' }}>{content.slice(0, 80)}...</p>
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, transparent, rgba(8,6,12,0.85))' }}>
            <div className="flex items-center gap-2 px-4 py-1.5 rounded-full" style={{ background: lc.bg, border: `1px solid ${lc.primary}44` }}>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-eye-off-line text-xs" style={{ color: `${lc.primary}bb` }} /></span>
              <span className="text-[10px] font-bold tracking-widest uppercase" style={{ color: `${lc.primary}bb` }}>コンテンツは非公開</span>
            </div>
          </div>
        </div>
        <div className="px-5 pb-4 flex items-center justify-between" style={{ borderTop: `1px solid ${lc.primary}15` }}>
          <div className="flex items-center gap-1.5 pt-3">
            {[1, 2, 3].map((lv) => (
              <div key={lv} className="w-2 h-2 rounded-full" style={{ background: lv <= requiredFanLevel ? lc.primary : 'rgba(255,255,255,0.1)', boxShadow: lv <= requiredFanLevel ? `0 0 6px ${lc.glow}` : 'none' }} />
            ))}
            <span className="text-[10px] tracking-widest ml-1.5" style={{ color: 'rgba(255,255,255,0.3)' }}>LV.{requiredFanLevel} REQUIRED</span>
          </div>
          <motion.button whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.96 }} onClick={handleArtistClick} className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold tracking-widest cursor-pointer whitespace-nowrap mt-3 transition-all duration-200" style={{ background: lc.bg, border: `1px solid ${lc.primary}55`, color: lc.primary, boxShadow: `0 0 16px ${lc.glow}` }}>
            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-up-line text-xs" /></span>
            アーティストページへ
          </motion.button>
        </div>
        <div className="h-px w-full" style={{ background: `linear-gradient(90deg, transparent, ${lc.primary}44, transparent)` }} />
      </motion.div>
    );
  }

  // ── 通常表示 ──────────────────────────────────────────
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{
          backgroundColor: 'rgba(63,63,70,0.5)',
        }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="rounded-2xl overflow-hidden bg-zinc-800/40 backdrop-blur-md border border-zinc-700/50"
        style={{
          boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
        }}
      >
        {/* 非表示コンテンツ表示中の警告バナー */}
        <AnimatePresence>
          {is_hidden && showHiddenContent && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="flex items-center justify-between px-golden-sm py-2 overflow-hidden"
              style={{ background: 'rgba(255,40,0,0.08)', borderBottom: '1px solid rgba(255,60,0,0.2)' }}
            >
              <div className="flex items-center gap-2">
                <i className="ri-alarm-warning-fill text-xs" style={{ color: '#ff5020' }} />
                <span className="text-[11px] tracking-wider" style={{ color: 'rgba(255,120,60,0.8)' }}>通報により非表示にされたコンテンツを表示中</span>
              </div>
              <button onClick={() => setShowHiddenContent(false)} className="flex items-center gap-1 text-[11px] cursor-pointer whitespace-nowrap transition-opacity hover:opacity-70" style={{ color: 'rgba(255,255,255,0.3)' }}>
                <i className="ri-eye-off-line text-xs" />
                再び隠す
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* UNLOCKED特別ライン */}
        {isUnlockedTab && !is_hidden && (
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,215,0,0.6), rgba(0,255,65,0.4), transparent)' }} />
        )}

        {/* NODE告知ライン */}
        {isNode && !is_hidden && !isUnlockedTab && (
          <div className="h-px w-full bg-brand-primary/60" />
        )}

        {/* アーティストカラーライン（通常） */}
        {!isNode && !isUnlockedTab && !is_hidden && (
          <div className="h-px w-full" style={{ background: `linear-gradient(90deg, transparent, ${artistColor}66, transparent)` }} />
        )}

        {/* ── Header ── */}
        <div className="px-golden-base pt-golden-base pb-golden-sm flex items-center gap-golden-sm">
          <button
            onClick={handleArtistClick}
            className="relative w-11 h-11 rounded-full overflow-hidden flex-shrink-0 cursor-pointer transition-all duration-300"
            style={{ border: `2px solid ${isUnlockedTab ? 'rgba(255,215,0,0.45)' : isNode ? 'rgba(34,211,238,0.3)' : `${artistColor}44`}` }}
          >
            <img src={artistAvatar} alt={artistName} className="w-full h-full object-cover" />
          </button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-golden-xs flex-wrap">
              <button
                onClick={handleArtistClick}
                className="font-display text-[0.95rem] tracking-wider transition-all duration-200 cursor-pointer text-zinc-100"
                onMouseEnter={(e) => { e.currentTarget.style.color = isNode ? '#22d3ee' : isUnlockedTab ? '#ffd700' : artistColor; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#f4f4f5'; }}
              >
                {artistName}
              </button>
              {isNode && (
                <span className="inline-flex items-center gap-1 px-golden-xs py-[0.18rem] rounded-golden-xs text-[10px] font-bold tracking-widest uppercase" style={{ background: 'rgba(34,211,238,0.1)', border: '1px solid rgba(34,211,238,0.35)', color: '#22d3ee' }}>
                  <i className="ri-store-2-line text-[10px]" />NODE
                </span>
              )}
              {isUnlockedTab && (
                <span className="inline-flex items-center gap-1 px-golden-xs py-[0.18rem] rounded-golden-xs text-[10px] font-bold tracking-widest uppercase" style={{ background: 'rgba(255,215,0,0.1)', border: '1px solid rgba(255,215,0,0.35)', color: '#ffd700' }}>
                  <i className="ri-trophy-fill text-[10px]" />UNLOCKED
                </span>
              )}
              {isVideoEmbed && (
                <span className="inline-flex items-center gap-1 px-golden-xs py-[0.18rem] rounded-golden-xs text-[10px] font-bold tracking-widest uppercase bg-brand-secondary/10 border border-brand-secondary/30 text-brand-secondary">
                  <i className="ri-movie-2-line text-[10px]" />{mediaType.toUpperCase()}
                </span>
              )}
            </div>
            <p className="text-[0.618rem] tracking-widest mt-[0.2rem] text-zinc-500">{postDate}</p>
          </div>

          {requiredFanLevel > 0 && !isNode && (
            <div className="flex items-center gap-1 px-golden-sm py-[0.3rem] rounded-golden-full flex-shrink-0 bg-brand-secondary/10 border border-brand-secondary/30">
              <i className="ri-vip-crown-fill text-[10px] text-brand-secondary" />
              <span className="text-[10px] font-bold tracking-wider text-brand-secondary">Lv.{requiredFanLevel}+</span>
            </div>
          )}

          {/* More Menu */}
          <div className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-200 cursor-pointer"
              style={{ background: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(63,63,70,0.5)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              <i className="ri-more-2-fill text-base text-zinc-500" />
            </button>
            {isMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsMenuOpen(false)} />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -8 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 top-9 z-20 w-52 rounded-2xl overflow-hidden bg-zinc-900/98 backdrop-blur-md border border-zinc-700/50"
                  style={{
                    boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                  }}
                >
                  <button
                    onClick={() => { setIsMenuOpen(false); setIsReportModalOpen(true); }}
                    className="w-full px-golden-base py-golden-sm flex items-center gap-golden-sm hover:bg-red-500/10 transition-colors cursor-pointer"
                  >
                    <i className="ri-alarm-warning-fill text-red-500 text-base" />
                    <span className="text-red-500 text-[0.764rem] font-bold tracking-wide">この投稿を通報する</span>
                  </button>
                </motion.div>
              </>
            )}
          </div>
        </div>

        {/* ── Media ── */}
        {imageUrl && (
          <div className="relative w-full px-golden-sm">
            {isVideoEmbed ? (
              <div className="rounded-2xl overflow-hidden border border-zinc-700/50">
                <EmbedPlayer url={imageUrl} accentColor={isNode ? '#22d3ee' : '#22d3ee'} compact={false} autoplay={false} />
              </div>
            ) : (
              <div
                className="rounded-2xl overflow-hidden border border-zinc-700/50"
                style={{
                  aspectRatio: '16/9',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                }}
              >
                <img src={imageUrl} alt="Post visual" className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-[1.02]" />
              </div>
            )}
          </div>
        )}

        {/* ── Content ── */}
        <div
          className="px-golden-base pb-golden-sm"
          style={{ paddingTop: imageUrl ? '0.618rem' : '0' }}
        >
          <p
            className="text-golden-base whitespace-pre-wrap tracking-wide text-zinc-300"
            style={{
              lineHeight: '1.618',
              fontSize: '1rem',
            }}
          >
            {linkifyText(content)}
          </p>
        </div>

        {/* ── Action Bar ── */}
        <div
          className="px-golden-base pb-golden-base border-t border-zinc-700/50"
          style={{ paddingTop: '0.618rem' }}
        >
          {!isNode && localSupportTotal >= 100 && (
            <div className="flex items-center gap-golden-xs mb-golden-sm">
              <i className="ri-fire-fill text-xs text-brand-secondary" />
              <span
                className="text-[0.618rem] tracking-widest font-bold uppercase text-brand-secondary/80"
              >
                {localSupportTotal.toLocaleString()} pt RESONATED
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            {/* 左側：ECHO / SAVE / DIFFUSE */}
            <div className="flex items-center gap-golden-xs">

              {/* ECHO */}
              <ActionButton
                onClick={handleEcho}
                isActive={isEchoed}
                activeColor="#fb7185"
                glowColor="rgba(251,113,133,0.35)"
                icon={isEchoed ? 'ri-heart-fill' : 'ri-heart-line'}
                label="ECHO"
                anim={echoAnim}
                particleColor="#fb7185"
              />

              {/* SAVE */}
              <ActionButton
                onClick={handleSave}
                isActive={isSaved}
                activeColor="#fbbf24"
                glowColor="rgba(251,191,36,0.35)"
                icon={isSaved ? 'ri-bookmark-fill' : 'ri-bookmark-line'}
                label="SAVE"
                anim={saveAnim}
                particleColor="#fbbf24"
                animType="wobble"
              />

              {/* DIFFUSE */}
              <ActionButton
                onClick={handleShare}
                isActive={false}
                activeColor="rgba(244,244,245,0.6)"
                glowColor="rgba(244,244,245,0.15)"
                icon="ri-share-forward-line"
                label="DIFFUSE"
                anim={false}
                particleColor="rgba(244,244,245,0.5)"
              />
            </div>

            {/* 右側：RESONATE */}
            {!isNode && (
              <motion.button
                whileHover={{ backgroundColor: 'rgba(251,113,133,0.9)' }}
                whileTap={{ scale: 0.98 }}
                onClick={() => { if (!user) return; setIsResonateModalOpen(true); }}
                className="relative flex items-center gap-golden-xs px-golden-base py-[0.5rem] rounded-full font-bold text-[0.618rem] tracking-widest uppercase cursor-pointer whitespace-nowrap overflow-hidden transition-all duration-200 bg-brand-secondary text-white"
                style={{
                  boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                }}
              >
                <motion.span
                  animate={{ scale: [1, 1.22, 1] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                  className="w-4 h-4 flex items-center justify-center"
                >
                  <i className="ri-ripple-line text-sm" />
                </motion.span>
                <span>RESONATE</span>
                <span className="text-[0.55rem] font-black opacity-75 ml-[0.2rem]">100pt</span>
              </motion.button>
            )}

            {/* NODE用アナウンスラベル */}
            {isNode && (
              <div className="flex items-center gap-golden-xs">
                <i className="ri-megaphone-line text-sm text-brand-primary" />
                <span className="text-[0.618rem] tracking-widest font-bold text-brand-primary">NODE ANNOUNCEMENT</span>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      <ReportModal isOpen={isReportModalOpen} onClose={() => setIsReportModalOpen(false)} postId={id} />

      {!isNode && (
        <ResonateModal
          isOpen={isResonateModalOpen}
          onClose={() => setIsResonateModalOpen(false)}
          artistId={artistId}
          artistName={artistName}
          displayColor={artistColor}
          onSuccess={handleResonateSuccess}
        />
      )}

      {levelUpInfo && (
        <LevelUpOverlay
          newLevel={levelUpInfo.newLevel}
          accentColor={levelUpInfo.accentColor}
          artistName={levelUpInfo.artistName}
          onComplete={clearLevelUp}
        />
      )}
    </>
  );
}

// ── ActionButton サブコンポーネント ──────────────────────────────
interface ActionButtonProps {
  onClick: () => void;
  isActive: boolean;
  activeColor: string;
  glowColor: string;
  icon: string;
  label: string;
  anim: boolean;
  particleColor: string;
  animType?: 'burst' | 'wobble';
}

function ActionButton({
  onClick,
  isActive,
  activeColor,
  glowColor,
  icon,
  label,
  anim,
  particleColor,
  animType = 'burst',
}: ActionButtonProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.button
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      className="relative flex items-center gap-[0.382rem] px-[0.618rem] py-[0.45rem] rounded-2xl transition-all duration-200 cursor-pointer overflow-visible"
      style={{
        background: isActive ? `${activeColor}14` : hovered ? 'rgba(63,63,70,0.5)' : 'transparent',
      }}
      title={label}
    >
      {/* バースト パーティクル */}
      <AnimatePresence>
        {anim && animType === 'burst' && (
          <>
            {[...Array(6)].map((_, i) => (
              <motion.span
                key={i}
                initial={{ opacity: 1, scale: 0, x: 0, y: 0 }}
                animate={{
                  opacity: 0,
                  scale: 1,
                  x: Math.cos((i / 6) * Math.PI * 2) * 18,
                  y: Math.sin((i / 6) * Math.PI * 2) * 18,
                }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
                className="absolute w-1 h-1 rounded-full pointer-events-none"
                style={{ background: particleColor, left: '50%', top: '50%', marginLeft: '-2px', marginTop: '-2px' }}
              />
            ))}
          </>
        )}
      </AnimatePresence>

      {/* アイコン */}
      <motion.span
        animate={
          anim
            ? animType === 'wobble'
              ? { rotate: [0, -14, 14, 0], scale: [1, 1.28, 1] }
              : { scale: [1, 1.38, 1] }
            : isActive
            ? { scale: [1, 1.18, 1] }
            : { scale: 1 }
        }
        transition={{ duration: 0.35 }}
        className="relative w-5 h-5 flex items-center justify-center z-10"
      >
        <i
          className={`${icon} text-[1.05rem]`}
          style={{
            color: isActive ? activeColor : hovered ? `${activeColor}bb` : 'rgba(161,161,170,0.5)',
            transition: 'color 0.2s',
          }}
        />
      </motion.span>

      {/* ラベル */}
      <span
        className="text-[0.618rem] font-bold tracking-widest uppercase hidden sm:inline z-10"
        style={{
          color: isActive ? activeColor : hovered ? `${activeColor}99` : 'rgba(161,161,170,0.4)',
          transition: 'color 0.2s',
        }}
      >
        {label}
      </span>
    </motion.button>
  );
}
